export interface DeepCompareOptions {
  checkArrayOrder?: boolean;     // default: true
  strictKeys?: boolean;          // default: false → lenient on extra keys
  logOnMismatch?: boolean;       // default: false (changed from your last version)
  ignoreKeys?: string[];         // keys to ignore / skip during comparison
}

export function deepObjectsCompare(
  a: any,
  b: any,
  options: DeepCompareOptions = {}
): boolean {
  const {
    checkArrayOrder = true,
    strictKeys = false,
    logOnMismatch = false,
    ignoreKeys = [],
  } = options;

  const log = (...args: any[]) => {
    if (logOnMismatch) console.log(...args);
  };

  // Both falsy → equal
  if (!a && !b) return true;

  // One falsy, one truthy → not equal
  if (!a || !b) {
    log('Mismatch: one is falsy, the other is not', { a, b });
    return false;
  }

  // Different types
  if (typeof a !== typeof b) {
    log('Mismatch: different types', { typeA: typeof a, typeB: typeof b, a, b });
    return false;
  }

  // Same reference (fast path)
  if (a === b) return true;

  // Primitives (both truthy) → strict equality
  if (typeof a !== 'object' || a === null || b === null) {
    if (a !== b) {
      log('Primitive mismatch', { a, b });
    }
    return a === b;
  }

  // One is array, one is not
  if (Array.isArray(a) !== Array.isArray(b)) {
    log('Mismatch: one is array, the other is not', { a, b });
    return false;
  }

  if (Array.isArray(a)) {
    if (a.length !== b.length) {
      log('Array length mismatch', { lengthA: a.length, lengthB: b.length });
      return false;
    }

    if (checkArrayOrder) {
      // Ordered comparison
      for (let i = 0; i < a.length; i++) {
        if (!deepObjectsCompare(a[i], b[i], options)) {
          log(`Array index ${i} mismatch`, { valueA: a[i], valueB: b[i] });
          return false;
        }
      }
    } else {
      // Unordered (multiset) comparison
      const bCopy = [...b];
      for (let i = 0; i < a.length; i++) {
        const index = bCopy.findIndex((bItem) =>
          deepObjectsCompare(a[i], bItem, options)
        );
        if (index === -1) {
          log(`Array element not found in target (unordered mode)`, {
            missing: a[i],
            remainingInB: bCopy,
          });
          return false;
        }
        bCopy.splice(index, 1);
      }
    }
    return true;
  }

  // ── Plain objects ────────────────────────────────────────────────

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  // Filter out ignored keys from comparison
  const relevantKeysA = keysA.filter(key => !ignoreKeys.includes(key));

  // Optional: enforce same number of keys (only counting relevant keys)
  if (strictKeys) {
    const relevantKeysB = keysB.filter(key => !ignoreKeys.includes(key));
    if (relevantKeysA.length !== relevantKeysB.length) {
      log('Object relevant key count mismatch (after ignoring keys)', {
        relevantKeysACount: relevantKeysA.length,
        relevantKeysBCount: relevantKeysB.length,
        ignoredKeys: ignoreKeys,
        extraInA: relevantKeysA.filter(k => !relevantKeysB.includes(k)),
        extraInB: relevantKeysB.filter(k => !relevantKeysA.includes(k)),
      });
      return false;
    }
  }

  for (const key of relevantKeysA) {
    // Skip ignored keys
    if (ignoreKeys.includes(key)) {
      continue;
    }

    // Optional strict key existence check
    if (strictKeys && !Object.prototype.hasOwnProperty.call(b, key)) {
      log(`Key "${key}" exists in A but not in B (not ignored)`);
      return false;
    }

    // Compare value (even if key missing in b → will fail in recursive call)
    if (!deepObjectsCompare(a[key], b[key], options)) {
      log(`Value mismatch at key "${key}"`, {
        valueA: a[key],
        valueB: b[key],
      });
      return false;
    }
  }

  return true;
}